import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "asynchronous-and-deferred-operations"
    }}>{`Asynchronous and deferred operations`}</h1>
    <p>{`Asynchronous operations are not native to PHP but it is still possible to execute heavy
operations simultaneously, or delay them until they absolutely have to be finished.`}</p>
    <p>{`To make writing asynchronous code easier, Adobe Commerce and Magento Open Source provide the `}<inlineCode parentName="p">{`DeferredInterface`}</inlineCode>{` to use with asynchronous operations.
This allows client code to work with asynchronous operations just as it would with standard operations.`}</p>
    <h2 {...{
      "id": "deferredinterface"
    }}>{`DeferredInterface`}</h2>
    <p><inlineCode parentName="p">{`Magento\\Framework\\Async\\DeferredInterface`}</inlineCode>{` is quite simple:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface DeferredInterface
{
    /**
     * @return mixed Value.
     * @throws \\Throwable
     */
    public function get();

    public function isDone(): bool;
}
`}</code></pre>
    <p>{`When the client code needs the result, the `}<inlineCode parentName="p">{`get()`}</inlineCode>{` method will be called to retrieve the result.
`}<inlineCode parentName="p">{`isDone()`}</inlineCode>{` can be used to see whether the code has completed.`}</p>
    <p>{`There are 2 types of asynchronous operations where `}<inlineCode parentName="p">{`DeferredInterface`}</inlineCode>{` can be used to describe the result:`}</p>
    <ul>
      <li parentName="ul">{`With asynchronous operations in progress, calling `}<inlineCode parentName="li">{`get()`}</inlineCode>{` would wait for them to finish and return their result.`}</li>
      <li parentName="ul">{`With deferred operations, `}<inlineCode parentName="li">{`get()`}</inlineCode>{` would actually start the operation, wait for it to finish, and then return the result.`}</li>
    </ul>
    <p>{`Sometimes developers require more control over long asynchronous operations.
That is why there is an extended deferred variant - `}<inlineCode parentName="p">{`Magento\\Framework\\Async\\CancelableDeferredInterface`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface CancelableDeferredInterface extends DeferredInterface
{
    /**
     * @param bool $force Cancel operation even if it's already started.
     * @return void
     * @throws CancelingDeferredException When failed to cancel.
     */
    public function cancel(bool $force = false): void;

    /**
     * @return bool
     */
    public function isCancelled(): bool;
}
`}</code></pre>
    <p>{`This interface is for operations that may take too long and can be canceled.`}</p>
    <h3 {...{
      "id": "client-code"
    }}>{`Client code`}</h3>
    <p>{`Assuming that `}<inlineCode parentName="p">{`serviceA`}</inlineCode>{`, `}<inlineCode parentName="p">{`serviceB`}</inlineCode>{` and `}<inlineCode parentName="p">{`serviceC`}</inlineCode>{` all execute asynchronous operations, such as HTTP requests, the client code would look like:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`public function aMethod() {
    //Started executing 1st operation
    $operationA = $serviceA->executeOp();

    //Executing 2nd operations at the same time
    $operationB = $serviceB->executeOp2();

    //We need to wait for 1st operation to start operation #3
    $serviceC->executeOp3($operationA->get());

    //We don't have to wait for operation #2, let client code wait for it if it needs the result
    //Operation number #3 is being executed simultaneously with operation #2
    return $operationB;
}
`}</code></pre>
    <p>{`And not a callback in sight!`}</p>
    <p>{`With the deferred client, the code can start multiple operations at the same time, wait for operations required to finish and pass the promise of a result to another method.`}</p>
    <h2 {...{
      "id": "proxydeferredfactory"
    }}>{`ProxyDeferredFactory`}</h2>
    <p>{`When writing a module or an extension, you may not want to burden other developers with having to know that your method is performing an asynchronous operation.
There is a way to hide it: employ the autogenerated factory `}<inlineCode parentName="p">{`YourClassName\\ProxyDeferredFactory`}</inlineCode>{`. With its help, you can return values that seem like regular objects
but are in fact deferred results.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`public function __construct(CallResult\\ProxyDeferredFactory $callResultFactory)
{
    $this->proxyDeferredFactory = $callResultFactory;
}

....

public function doARemoteCall(string $uniqueValue): CallResult
{
    //Async HTTP request, get() will return a CallResult instance.
    //Call is in progress.
    $deferredResult = $this->client->call($uniqueValue);

    //Returns CallResult instance that will call $deferredResult->get() when any of the object's methods is used.
    return $this->proxyDeferredFactory->create(['deferred' => $deferredResult]);
}

public function doCallsAndProcess(): Result
{
    //Both calls running simultaneously
    $call1 = $this->doARemoteCall('call1');
    $call2 = $this->doARemoteCall('call2');

    //Only when CallResult::getStuff() is called the $deferredResult->get() is called.
    return new Result([
        'call1' => $call1->getStuff(),
        'call2' => $call2->getStuff()
    ]);
}
`}</code></pre>
    <h2 {...{
      "id": "using-deferredinterface-for-background-operations"
    }}>{`Using DeferredInterface for background operations`}</h2>
    <p>{`As mentioned above, the first type of asynchronous operations are operations executing in a background.
`}<inlineCode parentName="p">{`DeferredInterface`}</inlineCode>{` can be used to give client code a promise of a not-yet-received result and wait for it by calling the `}<inlineCode parentName="p">{`get()`}</inlineCode>{` method.`}</p>
    <p>{`Take a look at an example: creating shipments for multiple products:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`class DeferredShipment implements DeferredInterface
{
    private $request;

    private $done = false;

    private $trackingNumber;

    public function __construct(AsyncRequest $request)
    {
        $this->request = $request;
    }

    public function isDone() : bool
    {
        return $this->done;
    }

    public function get()
    {
        if (!$this->trackingNumber) {
            $this->request->wait();
            $this->trackingNumber = json_decode($this->request->getBody(), true)['tracking'];

            $this->done = true;
        }

        return $this->trackingNumber;
    }
}

class Shipping
{
    ....

    public function ship(array $products): array
    {
        $shipments = [];
        //Shipping simultaneously
        foreach ($products as $product) {
            $shipments[] = new DeferredShipment(
                $this->client->sendAsync(['id' => $product->getId()])
            );
        }

        return $shipments;
    }
}

class ShipController
{
    ....

    public function execute(Request $request): Response
    {
        $shipments = $this->shipping->ship($this->products->find($request->getParam('ids')));
        $trackingsNumbers = [];
        foreach ($shipments as $shipment) {
            $trackingsNumbers[] = $shipment->get();
        }

        return new Response(['trackings' => $trackingNumbers]);
    }
}
`}</code></pre>
    <p>{`Here, multiple shipment requests are being sent at the same time with their results gathered later.
If you do not want to write your own `}<inlineCode parentName="p">{`DeferredInterface`}</inlineCode>{` implementation, you can use `}<inlineCode parentName="p">{`CallbackDeferred`}</inlineCode>{` to provide callbacks that will be used when `}<inlineCode parentName="p">{`get()`}</inlineCode>{` is called.`}</p>
    <h2 {...{
      "id": "using-deferredinterface-for-deferred-operations"
    }}>{`Using DeferredInterface for deferred operations`}</h2>
    <p>{`The second type of asynchronous operations are operations that are being postponed and executed only when a result is absolutely needed.`}</p>
    <p>{`An example:`}</p>
    <p>{`Assume you are creating a repository for an entity and you have a method that returns a singular entity by ID.
You want to make a performance optimization for cases when multiple entities are requested during the same request-response process, so you would not load them separately.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`class EntityRepository
{
    private $requestedEntityIds = [];

    private $identityMap = [];

    ...

    /**
     * @return Entity[]
     */
    public function findMultiple(array $ids): array
    {
        .....

        //Adding found entities to the identity map be able to find them by ID.
        foreach ($found as $entity) {
            $this->identityMap[$entity->getId()] = $entity;
        }

        ....
    }

    public function find(string $id): Entity
    {
        //Adding this ID to the list of previously requested IDs.
        $this->requestedEntityIds[] = $id;

        //Returning deferred that will find all requested entities
        //and return the one with $id
        return $this->proxyDeferredFactory->createFor(
            Entity::class,
            new CallbackDeferred(
                function () use ($id) {
                    if (empty($this->identityMap[$id])) {
                        $this->findMultiple($this->requestedEntityIds);
                        $this->requestedEntityIds = [];
                    }

                    return $this->identityMap[$id];
                }
            )
        );
    }

    ....
}

class EntitiesController
{
    ....

    public function execute(): Response
    {
        //No actual DB query issued
        $criteria1Id = $this->entityService->getEntityIdWithCriteria1();
        $criteria2Id = $this->entityService->getEntityIdWithCriteria2();
        $criteria1Entity = $this->entityRepo->find($criteria1Id);
        $criteria2Entity = $this->entityRepo->find($criteria2Id);

        //Querying the DB for both entities only when getStringValue() is called the 1st time.
        return new Response(
            [
                'criteria1' => $criteria1Entity->getStringValue(),
                'criteria2' => $criteria2Entity->getStringValue()
            ]
        );
    }
}
`}</code></pre>
    <h2 {...{
      "id": "examples"
    }}>{`Examples`}</h2>
    <p>{`See our asynchronous HTTP client `}<inlineCode parentName="p">{`Magento\\Framework\\HTTP\\AsyncClientInterface`}</inlineCode>{` and `}<inlineCode parentName="p">{`Magento\\Shipping\\Model\\Shipping`}</inlineCode>{` with various `}<inlineCode parentName="p">{`Magento\\Shipping\\Model\\Carrier\\AbstractCarrierOnline`}</inlineCode>{` implementations to see how `}<inlineCode parentName="p">{`DeferredInterface`}</inlineCode>{` can be used to work with asynchronous code.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      